import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import {
  Rocket, CheckCircle2, Clock, Loader2, Send, Copy, Mail, AlertTriangle,
  ShieldCheck, GraduationCap, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { agentOnboardingAPI } from '@/lib/api';

const INVITE_META = {
  pending: { label: 'Awaiting agent', tone: 'bg-amber-100 text-amber-800', icon: Clock },
  completed: { label: 'Profile completed', tone: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', tone: 'bg-slate-200 text-slate-600', icon: AlertTriangle },
  expired: { label: 'Expired', tone: 'bg-rose-100 text-rose-700', icon: AlertTriangle },
};

/**
 * KR-only operative onboarding hub for an agent in `onboarding` status.
 * Shows the self-service invite status (+ resend), the onboarding checklist
 * (complete personal details + verified NPN), and a "Mark as Active" action
 * that graduates the agent out of onboarding.
 */
export function OnboardingPanel({ npn, onChanged }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [freshLink, setFreshLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [confirmGraduate, setConfirmGraduate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await agentOnboardingAPI.agentDetail(npn);
      setData(data);
    } catch (_) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [npn]);

  useEffect(() => { load(); }, [load]);

  const toggleItem = async (key, value) => {
    setBusy(key);
    try {
      const call = key === 'welcome_email_sent'
        ? agentOnboardingAPI.setWelcomeEmail(npn, value)
        : agentOnboardingAPI.verifyNpn(npn, value);
      const { data: res } = await call;
      setData((prev) => ({ ...prev, checklist: res.checklist }));
      const msgs = {
        welcome_email_sent: value ? 'Welcome email marked as sent' : 'Welcome email mark cleared',
        npn_verified: value ? 'NPN marked as verified' : 'NPN verification cleared',
      };
      toast.success(msgs[key] || 'Updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update');
    } finally {
      setBusy('');
    }
  };

  const resend = async () => {
    if (!data?.invite?.link_id) return;
    setBusy('resend');
    try {
      const { data: res } = await agentOnboardingAPI.resendLink(data.invite.link_id);
      setFreshLink(res.onboard_link);
      toast.success(res.email_sent ? `Invite re-emailed to ${res.email}` : 'Fresh link ready — copy and share it');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not resend the invite');
    } finally {
      setBusy('');
    }
  };

  const copyLink = async () => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(freshLink);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error('Could not copy — select the link manually'); }
  };

  const graduate = async () => {
    setBusy('graduate');
    try {
      await agentOnboardingAPI.graduate(npn);
      toast.success('Agent activated 🎉');
      setConfirmGraduate(false);
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not activate the agent');
    } finally {
      setBusy('');
    }
  };

  const deleteProfile = async () => {
    setBusy('delete');
    try {
      await agentOnboardingAPI.deleteAgent(npn);
      toast.success('Onboarding profile deleted');
      setConfirmDelete(false);
      navigate('/onboarding-workflows');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete the profile');
    } finally {
      setBusy('');
    }
  };

  if (loading || !data) return null;

  const cl = data.checklist || { items: [], pct: 0, completed: 0, total: 0, all_done: false };
  const invite = data.invite;
  const iMeta = invite ? (INVITE_META[invite.status] || INVITE_META.pending) : null;
  const InviteIcon = iMeta?.icon;

  return (
    <Card data-testid="agent-onboarding-panel" className="border-[color:var(--kr-gold-200)]">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--kr-navy-50)' }}>
              <Rocket size={18} style={{ color: 'var(--kr-navy-700)' }} />
            </div>
            <div>
              <p className="font-heading font-semibold text-slate-900">Onboarding workspace</p>
              <p className="text-xs text-slate-500">Finish these steps, then activate the agent.</p>
            </div>
          </div>
          <Button
            onClick={() => setConfirmGraduate(true)}
            disabled={busy === 'graduate'}
            data-testid="onboarding-graduate-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {busy === 'graduate' ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <GraduationCap size={15} className="mr-1.5" />}
            Mark as Active
          </Button>
        </div>

        {/* Progress */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Checklist</span>
            <span className="text-xs tabular-nums font-semibold" style={{ color: cl.all_done ? '#047857' : 'var(--kr-muted)' }}>
              {cl.completed}/{cl.total} complete
            </span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${cl.pct}%`, background: cl.all_done ? 'linear-gradient(90deg,#10b981,#059669)' : 'linear-gradient(90deg,var(--kr-navy-700),var(--kr-navy-900))' }} />
          </div>
        </div>

        {/* Checklist items */}
        <ul className="mt-3 space-y-2" data-testid="onboarding-checklist-items">
          {cl.items.map((it) => (
            <li key={it.key} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-3 py-2" data-testid={`onboarding-check-${it.key}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                {it.done ? <CheckCircle2 size={16} className="text-emerald-500 shrink-0" /> : <Clock size={16} className="text-slate-300 shrink-0" />}
                <div className="min-w-0">
                  <p className={`text-sm ${it.done ? 'text-slate-500' : 'text-slate-800 font-medium'}`}>{it.label}</p>
                  <p className="text-[11px] text-slate-400">{it.hint}</p>
                </div>
              </div>
              {it.auto ? (
                <Badge className={`text-[10px] shrink-0 ${it.done ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>Auto</Badge>
              ) : (
                <Switch
                  checked={it.done}
                  onCheckedChange={(v) => toggleItem(it.key, v)}
                  disabled={busy === it.key}
                  data-testid={`onboarding-toggle-${it.key}`}
                />
              )}
            </li>
          ))}
        </ul>

        {/* Missing profile data */}
        {(data.missing_fields || []).length > 0 && (
          <div className="mt-3" data-testid="onboarding-missing-fields">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Missing profile data</p>
            <div className="flex flex-wrap gap-1.5">
              {data.missing_fields.map((f) => (
                <span key={f.key} className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700 ring-1 ring-amber-200">
                  <AlertTriangle size={10} /> {f.label}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Invite status */}
        {invite && (
          <div className="mt-4 rounded-lg bg-slate-50 p-3" data-testid="onboarding-invite-block">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <Mail size={14} className="text-slate-400" />
                Self-service invite
                <Badge className={`text-[10px] ${iMeta.tone}`} data-testid="onboarding-invite-status">
                  <InviteIcon size={11} className="mr-1" /> {iMeta.label}
                </Badge>
              </div>
              {invite.status !== 'completed' && (
                <Button size="sm" variant="outline" className="h-8" onClick={resend} disabled={busy === 'resend'} data-testid="onboarding-invite-resend">
                  {busy === 'resend' ? <Loader2 size={13} className="animate-spin mr-1" /> : <Send size={13} className="mr-1" />}
                  Resend
                </Button>
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {invite.email}
              {invite.completed_at ? ` · completed ${new Date(invite.completed_at).toLocaleDateString()}`
                : invite.expires_at ? ` · expires ${new Date(invite.expires_at).toLocaleDateString()}` : ''}
            </div>
            {invite.status !== 'completed' && (invite.furthest_step > 0 || invite.opened_at) && (
              <div className="mt-2" data-testid="onboarding-invite-step-progress">
                {(() => {
                  const total = invite.total_steps || 5;
                  const reached = invite.furthest_step || (invite.opened_at ? 1 : 0);
                  const pct = Math.round((Math.min(reached, total) / total) * 100);
                  return (
                    <>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-medium text-slate-600">
                          Stopped at step {Math.min(reached, total)}/{total}
                          {invite.furthest_step_label ? `: ${invite.furthest_step_label}` : ''}
                        </span>
                        {invite.last_step_at && (
                          <span className="text-[10px] text-slate-400">{new Date(invite.last_step_at).toLocaleString()}</span>
                        )}
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'linear-gradient(90deg,var(--kr-navy-700),var(--kr-navy-900))' }} />
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
            {freshLink && (
              <div className="flex gap-2 mt-2">
                <Input readOnly value={freshLink} className="text-xs h-8" data-testid="onboarding-fresh-link" />
                <Button type="button" variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={copyLink} data-testid="onboarding-copy-link">
                  {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Danger zone — delete an agent still in onboarding */}
      <div className="px-5 pb-4 -mt-1">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-600 hover:text-rose-700"
          onClick={() => setConfirmDelete(true)}
          data-testid="onboarding-delete-btn"
        >
          <Trash2 size={13} /> Delete this onboarding profile
        </button>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="onboarding-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 font-heading">
              <AlertTriangle size={18} className="text-rose-600" /> Delete this onboarding profile?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the agent record, their portal login and any onboarding
              links. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="onboarding-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700"
              onClick={(e) => { e.preventDefault(); deleteProfile(); }}
              disabled={busy === 'delete'}
              data-testid="onboarding-delete-confirm"
            >
              {busy === 'delete' ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Trash2 size={14} className="mr-1.5" />}
              Delete profile
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={confirmGraduate} onOpenChange={setConfirmGraduate}>
        <DialogContent data-testid="onboarding-graduate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><ShieldCheck size={18} /> Activate this agent?</DialogTitle>
            <DialogDescription>
              {cl.all_done
                ? 'All onboarding steps are complete. The agent will move to Active and leave the onboarding roster.'
                : `${cl.total - cl.completed} checklist item(s) are still pending. You can still activate now — the agent will move to Active and leave the onboarding roster.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmGraduate(false)} data-testid="onboarding-graduate-cancel">Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={graduate} disabled={busy === 'graduate'} data-testid="onboarding-graduate-confirm">
              {busy === 'graduate' ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <GraduationCap size={15} className="mr-1.5" />}
              Activate agent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default OnboardingPanel;
